"use client";

import { useRef, useState } from "react";
import { Box, Cloud, Copy, Download, FilePlus2, FolderOpen, Maximize, MousePointer2, Move3D, Redo2, Rotate3D, Save, Scale3D, Trash2, Undo2, UserRound, Video } from "lucide-react";
import type { TransformTool } from "@/types/scene";
import { useEditorStore } from "@/stores/editorStore";
import { useProjectStore } from "@/stores/projectStore";
import { useSceneStore } from "@/stores/sceneStore";
import { createProject, downloadProject, parseProject, saveLocalProject } from "@/services/projectStorage";
import { useTimelineStore } from "@/stores/timelineStore";
import { useBackendStore } from "@/stores/backendStore";

const tools: { id: TransformTool; label: string; shortcut: string; icon: typeof MousePointer2 }[] = [
  { id: "select", label: "Select", shortcut: "Q", icon: MousePointer2 },
  { id: "translate", label: "Move", shortcut: "W", icon: Move3D },
  { id: "rotate", label: "Rotate", shortcut: "E", icon: Rotate3D },
  { id: "scale", label: "Scale", shortcut: "R", icon: Scale3D },
];

export function TopToolbar() {
  const fileInput = useRef<HTMLInputElement>(null);
  const [message, setMessage] = useState<string | null>(null);
  const activeTool = useEditorStore((state) => state.activeTool);
  const selectedId = useEditorStore((state) => state.selectedObjectId);
  const setActiveTool = useEditorStore((state) => state.setActiveTool);
  const selectObject = useEditorStore((state) => state.selectObject);
  const objects = useSceneStore((state) => state.objects);
  const past = useSceneStore((state) => state.past);
  const future = useSceneStore((state) => state.future);
  const undo = useSceneStore((state) => state.undo);
  const redo = useSceneStore((state) => state.redo);
  const duplicateObject = useSceneStore((state) => state.duplicateObject);
  const removeObject = useSceneStore((state) => state.removeObject);
  const replaceObjects = useSceneStore((state) => state.replaceObjects);
  const addFromAsset = useSceneStore((state) => state.addFromAsset);
  const projectId = useProjectStore((state) => state.projectId);
  const projectName = useProjectStore((state) => state.projectName);
  const setProject = useProjectStore((state) => state.setProject);
  const markSaved = useProjectStore((state) => state.markSaved);
  const clearTimeline = useTimelineStore((state) => state.clearTimeline);
  const replaceTimeline = useTimelineStore((state) => state.replaceTimeline);
  const removeObjectTracks = useTimelineStore((state) => state.removeObjectTracks);
  const backendStatus = useBackendStore((state) => state.status);
  const remoteUser = useBackendStore((state) => state.user);
  const openCloudPanel = useBackendStore((state) => state.openPanel);
  const startFullscreenPreview = useEditorStore((state) => state.startFullscreenPreview);
  const flash = (text: string) => { setMessage(text); window.setTimeout(() => setMessage(null), 2200); };
  const snapshot = () => createProject(projectName, objects, projectId, useTimelineStore.getState().tracks, useTimelineStore.getState().duration);
  const save = () => { saveLocalProject(snapshot()); markSaved(); flash("Project saved locally"); };
  const duplicate = () => { if (!selectedId) return; const id = duplicateObject(selectedId); if (id) selectObject(id); };
  const remove = () => { if (!selectedId) return; const hasTracks = useTimelineStore.getState().tracks.some((track) => track.objectId === selectedId); if (hasTracks && !window.confirm("This object has timeline actions. Delete the object and all related clips?")) return; removeObjectTracks(selectedId); removeObject(selectedId); selectObject(null); };
  const addCamera = () => { const id = addFromAsset("camera01"); if (id) selectObject(id); };
  const enterFullscreen = async () => {
    const target = document.getElementById("scene-editor");
    if (!target || !target.requestFullscreen) { flash("Fullscreen is not supported by this browser"); return; }
    const cameraId = objects.find((object) => object.type === "camera")?.id ?? null;
    try { startFullscreenPreview(cameraId); await target.requestFullscreen(); useTimelineStore.getState().restart(); }
    catch { useEditorStore.getState().endFullscreenPreview(); flash("Fullscreen request was blocked"); }
  };
  const newProject = () => {
    if (objects.length && !window.confirm("Create a new scene? Unsaved scene changes will be replaced.")) return;
    replaceObjects([], true); clearTimeline(); setProject(`project-${Date.now()}`, "Untitled Farm Scene"); selectObject(null); flash("New scene created");
  };
  const importFile = async (file?: File) => {
    if (!file) return;
    try {
      const project = parseProject(await file.text());
      replaceObjects(project.objects, true); replaceTimeline(project.timeline, project.duration); setProject(project.id, project.name); selectObject(null); saveLocalProject(project); flash("Project loaded");
    } catch (error) { flash(error instanceof Error ? error.message : "Could not load project"); }
    if (fileInput.current) fileInput.current.value = "";
  };
  return (
    <header className="topbar">
      <div className="brand"><span className="brand-mark"><Box size={17} /></span><span>SceneForge <span style={{ color: "#7f8b92", fontWeight: 400 }}>3D</span></span></div>
      <div className="toolbar-group project-actions" aria-label="Project actions">
        <button className="icon-button" onClick={newProject} title="New project"><FilePlus2 size={16} /></button>
        <button className="icon-button" onClick={save} title="Save locally (Ctrl+S)"><Save size={16} /></button>
        <button className="icon-button" onClick={() => downloadProject(snapshot())} title="Export project JSON"><Download size={16} /></button>
        <button className="icon-button" onClick={() => fileInput.current?.click()} title="Load project JSON"><FolderOpen size={16} /></button>
        <input ref={fileInput} hidden type="file" accept="application/json,.json" onChange={(event) => void importFile(event.target.files?.[0])} />
      </div>
      <button className="tool-button add-camera-button" onClick={addCamera} title="Add scene camera"><Video size={15} /><span>Add Camera</span></button>
      <div className="toolbar-group" aria-label="History actions">
        <button className="icon-button" disabled={!past.length} onClick={undo} title="Undo (Ctrl+Z)"><Undo2 size={16} /></button>
        <button className="icon-button" disabled={!future.length} onClick={redo} title="Redo (Ctrl+Shift+Z)"><Redo2 size={16} /></button>
        <button className="icon-button" disabled={!selectedId} onClick={duplicate} title="Duplicate (Ctrl+D)"><Copy size={16} /></button>
        <button className="icon-button danger" disabled={!selectedId} onClick={remove} title="Delete selected"><Trash2 size={16} /></button>
      </div>
      <div className="toolbar-group" aria-label="Transform tools">
        {tools.map(({ id, label, shortcut, icon: Icon }) => (
          <button key={id} className={`tool-button ${activeTool === id ? "active" : ""}`} onClick={() => setActiveTool(id)} title={`${label} (${shortcut})`}><Icon size={15} /><span>{label}</span></button>
        ))}
      </div>
      <span className="toolbar-spacer" />
      {message && <span className="toolbar-message" role="status">{message}</span>}
      <button className="fullscreen-toolbar-button" onClick={() => void enterFullscreen()} title="Fullscreen Preview"><Maximize size={14} /><span>Fullscreen</span></button>
      <button className={`cloud-toolbar-button ${remoteUser ? "signed-in" : ""}`} onClick={openCloudPanel} title={remoteUser ? `Cloud projects — ${remoteUser.email}` : "Cloud projects"}>{remoteUser ? <UserRound size={14} /> : <Cloud size={14} />}<span>{backendStatus === "checking" ? "Checking…" : remoteUser ? remoteUser.email : backendStatus === "available" ? "Sign in" : "Local only"}</span></button>
      <span className="viewport-badge">Perspective • Shaded</span>
    </header>
  );
}
